import * as React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Layout } from 'antd';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import Link from 'next/link';
import { useRouter } from 'next/router';

import * as accountActions from '@portal/store/src/account/actions';
import { selectFirstAccount } from '@portal/store/src/account/selectors';
import * as payableActions from '@portal/store/src/payable/actions';
import { viewContextSelector } from '@portal/store/src/view-context/selectors';

import { LayoutWithSidebar } from '@/components/layout-with-sidebar';
import { RightSidebarContent } from '@/components/right-sidebar-content';
import { SelfServiceHomeRegular } from '@/components/self-service/home/index-regular';
import SelfServiceHomeTrialExpired from '@/components/self-service/home/index-trial-expired';
import { IntegrationsTableDataSelector } from '@/resources/integrations/selectors';
import { latestInvoicesSelector } from '@/resources/latest-invoices/selectors';
import { getFreeTrialDaysLeft } from '@/resources/saas/helpers';
import {
  saasUserPlanSelector,
  saasUserTrialSelector,
} from '@/resources/saas/selectors';
import { selectSaasSubscription } from '@/resources/saas/selects';
import { selfServiceDataSelector } from '@/resources/self-service/selectors';
import { isPermanentTrial } from '@/resources/supply-migration/helpers';
import { selectSaasMigrationSettings } from '@/resources/supply-migration/selects';
import { selectSupplySubAccounts } from '@/resources/supply-sub-accounts/internal/selectors';
import { nameSelector } from '@/resources/users/selectors';
import * as complianceActions from '@/store/compliance/actions';
import * as saasActions from '@/store/saas/actions';
import * as subAccountActions from '@/store/subaccount/actions';
import * as supplyContractActions from '@/store/supply-contracts/actions';
import * as supplyMigrationActions from '@/store/supply-migration/actions';

const ControlCenterHome: React.FC = () => {
  const { query: queryUnpicked, pathname, replace } = useRouter();
  const account = useSelector(selectFirstAccount);
  const clientName = useSelector(nameSelector);
  const integrations = useSelector(IntegrationsTableDataSelector);
  const latestInvoices = useSelector(latestInvoicesSelector);

  const saasSubscription = useSelector(selectSaasSubscription);
  const saasUserPlan = useSelector(saasUserPlanSelector);
  const saasUserTrial = useSelector(saasUserTrialSelector);
  const selfServiceData = useSelector(selfServiceDataSelector);
  const subAccounts = useSelector(selectSupplySubAccounts);
  const supplyMigration = useSelector(selectSaasMigrationSettings);

  const {
    showHomepageGuide,
    hasCrmUserRole,
    shouldRedirectToWelcome,
    shouldSkipTrialExpiredPage,
    showControlCenterHomeGettingStartedBox,
    showControlCenterHomeSetupBankingInformation,
    showControlCenterHomeHeaderText,
    showControlCenterSidebarTrialBanner,
    showSidebarResources,
  } = useSelector(viewContextSelector);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(supplyContractActions.getSupplyContractsAll.request({}));
    dispatch(saasActions.saasAllRequestAction.request({}));
    dispatch(complianceActions.myAccountComplianceRequestAction.request({}));
    dispatch(payableActions.payableRequestAction.request({}));
    dispatch(accountActions.accountRequestAction.request({}));
    dispatch(subAccountActions.supplySubAccountRequestAction.request({}));
    dispatch(
      supplyMigrationActions.getSettingsByAuthContextRequestAction.request({})
    );
  }, [dispatch]);

  useEffect(() => {
    if (isEmpty(saasUserTrial) && shouldRedirectToWelcome) {
      replace('/welcome');
    }
  }, [saasUserTrial]);

  if (hasCrmUserRole) {
    replace('/performance-summary');

    return <></>;
  }

  const query = pick(queryUnpicked, [
    'publishSuccess',
    'feePaymentSuccess',
    'upgradedMigrationSuccess',
  ]);

  const isPaid = Boolean(selfServiceData.currentPlan);

  const freeTrialEndDate = saasUserTrial?.endDate;
  const freeTrialDaysLeft = getFreeTrialDaysLeft(freeTrialEndDate);
  const freeTrialExpired = freeTrialDaysLeft < 0;

  const isPermanent = isPermanentTrial(supplyMigration!);

  return (
    <>
      <div className="self-service-home-scope">
        {showControlCenterHomeHeaderText && (
          <h1>
            Welcome, <strong>{clientName}</strong>!
          </h1>
        )}
        {freeTrialExpired &&
        !saasSubscription &&
        !supplyMigration &&
        !shouldSkipTrialExpiredPage ? (
          <Layout>
            <SelfServiceHomeTrialExpired
              freeTrialDaysLeft={freeTrialDaysLeft}
            />
          </Layout>
        ) : (
          <LayoutWithSidebar
            sidebarContent={
              <RightSidebarContent
                freeTrialEndDate={freeTrialEndDate}
                hideFreeTrial={isPaid}
                isPermanent={isPermanent}
                showResources={showSidebarResources}
                showTrialBanner={showControlCenterSidebarTrialBanner}
                supplyMigration={supplyMigration}
              />
            }
          >
            {showHomepageGuide && (
              <div className="new-feature">
                Learn more with&nbsp;
                <Link href="/guide/home">
                  <a>tutorial</a>
                </Link>
              </div>
            )}
            <SelfServiceHomeRegular
              account={account}
              clientName={clientName}
              integrations={integrations}
              isPermanent={isPermanent}
              latestInvoices={latestInvoices}
              pathname={pathname}
              query={query}
              saasUserPlan={saasUserPlan}
              selfServiceData={selfServiceData}
              showControlCenterHomeGettingStartedBox={
                showControlCenterHomeGettingStartedBox
              }
              showControlCenterHomeSetupBankingInformation={
                showControlCenterHomeSetupBankingInformation
              }
              subAccounts={subAccounts}
              supplyMigration={supplyMigration}
            />
          </LayoutWithSidebar>
        )}
      </div>
    </>
  );
};

export default ControlCenterHome;
